import type { AppNotification } from '../../services/notificationsService';

/**
 * Where tapping a notification should take the user. Pure so it's testable;
 * returns null when there's nowhere sensible to go (still mark it read).
 *
 * `context` picks hub vs lite routes — inspectors live in /lite and hub
 * routes would bounce them through the role redirect.
 */
export function notificationDeepLink(
  n: Pick<AppNotification, 'reference_type' | 'reference_id' | 'data'>,
  context: 'hub' | 'lite' = 'hub'
): string | null {
  const data = (n.data ?? {}) as { site_id?: string; chain?: string };

  if (n.reference_type === 'inspection') {
    // Close-out chain notifications land on the site — that's where the
    // close-out action happens, not on the inspection record.
    if (data.chain === 'final_inspection' && data.site_id) {
      return context === 'lite'
        ? `/lite/sites/${data.site_id}`
        : `/dashboard/sites/${data.site_id}`;
    }
    if (context === 'lite') {
      return data.site_id ? `/lite/sites/${data.site_id}` : '/lite/schedule';
    }
    return n.reference_id ? `/dashboard/inspections/${n.reference_id}` : null;
  }

  if (n.reference_type === 'site' && n.reference_id) {
    return context === 'lite'
      ? `/lite/sites/${n.reference_id}`
      : `/dashboard/sites/${n.reference_id}`;
  }

  return null;
}

/**
 * A rain event alert: field work for the inspector it was sent to. It shows
 * on the Lite dashboard and stays off the Hub one (Jim 2026-10-05: "I don't
 * think these should be showing on the hub side").
 */
export function isRainEventNotice(
  n: Pick<AppNotification, 'reference_type' | 'data'>
): boolean {
  return (
    n.reference_type === 'inspection' &&
    typeof (n.data ?? {}).rainfall_inches === 'number'
  );
}
